import React, { FC, useRef } from 'react';
import Resizer from '../Resizer';
import { isTouch } from '../../../../components/utils/browser';
import { useEditorContainerSize } from '../../../../components/Editor/Context';
import { useWidth } from '../../hooks/useWidth';
import { PapiDeviation } from '@wix/da-papi-types';

import viewerStyles from '../../Viewer/Viewer.scss';

export interface Props {
  deviations: PapiDeviation[];
  layout: 'torpedo' | 'grid';
  layoutSpacing: number | null;
  layoutRowAlignment: 'left' | 'center' | 'right';
  itemCropping: 'crop' | 'fit';
  gridItemWidth: number | null;
  gridItemHeight: number | null;
  torpedoRowHeight: number | null;
  linkOverrides: Record<number, string>;
  alignment: 'left' | 'center' | 'right';

  Renderer: React.FC<any>;
  onResizeEnd?: (width: number, height: number) => void;
  extOptions?: any;
  attrWidth: number | null;
  defaultWidth: number;
  editorDomRef: any;
}

const DaGallery: FC<Props> = ({
  deviations,
  layout,
  layoutSpacing,
  layoutRowAlignment,
  itemCropping,
  gridItemWidth,
  gridItemHeight,
  torpedoRowHeight,
  linkOverrides,
  Renderer,
  onResizeEnd,
  extOptions,
  attrWidth,
  defaultWidth,
  editorDomRef,
  alignment,
}) => {
  const containerSize = useEditorContainerSize();
  const blockRef = useRef<HTMLDivElement>(null);

  const { width, maxWidth } = useWidth(
    attrWidth,
    defaultWidth,
    extOptions.smallMedia,
    containerSize ?? 0
  );

  if (!deviations) {
    return null;
  }

  return (
    <div
      className={viewerStyles['deviation-gallery']}
      ref={blockRef}
      style={{ width }}
      data-dagallery="true"
      data-da-type="da-gallery"
      data-width={attrWidth}
      data-alignment={alignment ?? extOptions.defaultAlignment}
      data-layout={layout ?? extOptions.defaultLayout}
      data-layoutspacing={layoutSpacing ?? extOptions.defaultLayoutSpacing}
      data-layoutrowalignment={layoutRowAlignment}
      data-itemcropping={itemCropping ?? 'crop'}
      data-griditemwidth={gridItemWidth ?? extOptions.defaultGridItemWidth}
      data-griditemheight={gridItemHeight ?? extOptions.defaultGridItemHeight}
      data-torpedorowheight={
        torpedoRowHeight ?? extOptions.defaultTorpedoRowHeight
      }
    >
      <div
        style={{
          maxHeight: '100%',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <Renderer
          deviations={deviations}
          width={width}
          extOptions={extOptions}
          linkOverrides={linkOverrides}
          layoutSpacing={layoutSpacing}
          layoutRowAlignment={layoutRowAlignment}
          itemCropping={itemCropping}
          gridItemWidth={gridItemWidth}
          gridItemHeight={gridItemHeight}
          torpedoRowHeight={torpedoRowHeight}
          layout={layout}
        />
      </div>
      {!isTouch() && extOptions.canResize && (
        <Resizer
          key={width}
          editorDom={editorDomRef.current}
          imageRef={blockRef}
          minWidth={10}
          maxWidth={maxWidth}
          minHeight={10}
          maxHeight={Infinity}
          keepRatio={true}
          onResizeEnd={onResizeEnd}
        />
      )}
    </div>
  );
};
DaGallery.displayName = 'DaGallery';

export default DaGallery;
